import { NavMenu } from "@shopify/app-bridge-react";
import { AppProvider, Link } from "@shopify/polaris";
import "@shopify/polaris/build/esm/styles.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BaseSyntheticEvent, lazy, StrictMode, Suspense, useCallback } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { BrowserRouter, Route, Routes, useNavigate } from "react-router";
import "./App.scss";
import { FullPageError } from "./elements/FullPageError";
import { FullPageLoader } from "./elements/FullPageLoader";
import { useInstallation } from "./hooks/use-installation";
import { InstallationProvider } from "./providers/InstallationProvider";

const SyncHistory = lazy(() => import("./pages/history/SyncHistory").then((d) => ({ default: d.SyncHistory })));
const SyncHistoryDetail = lazy(() =>
  import("./pages/history/SyncHistoryDetail").then((d) => ({ default: d.SyncHistoryDetail })),
);
const Home = lazy(() => import("./pages/Home").then((d) => ({ default: d.Home })));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy").then((d) => ({ default: d.PrivacyPolicy })));
const ImportProducts = lazy(() => import("./pages/ImportProducts").then((d) => ({ default: d.ImportProducts })));
const Plans = lazy(() => import("./pages/Plans").then((d) => ({ default: d.Plans })));
const ListiniRuns = lazy(() => import("./pages/ListiniRuns").then((d) => ({ default: d.ListiniRuns })));
const ListiniRunRows = lazy(() => import("./pages/ListiniRunRows").then((d) => ({ default: d.ListiniRunRows })));
const SettingsHub = lazy(() => import("./pages/SettingsHub").then((d) => ({ default: d.SettingsHub })));
const DmodaSyncSettings = lazy(() =>
  import("./pages/settings/DmodaSyncSettings").then((d) => ({ default: d.DmodaSyncSettings })),
);
const DmodaImportProductsSettings = lazy(() =>
  import("./pages/settings/DmodaImportProductsSettings").then((d) => ({ default: d.DmodaImportProductsSettings })),
);
const DmodaImportPriceSettings = lazy(() =>
  import("./pages/settings/DmodaImportPriceSettings").then((d) => ({ default: d.DmodaImportPriceSettings })),
);
const DmodaShippingSettings = lazy(() =>
  import("./pages/settings/DmodaShippingSettings").then((d) => ({ default: d.DmodaShippingSettings })),
);
const DmodaRmaSettings = lazy(() =>
  import("./pages/settings/DmodaRmaSettings").then((d) => ({ default: d.DmodaRmaSettings })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AppProvider i18n={{}}>
          <Suspense fallback={<FullPageLoader message="Caricamento ..." />}>
            <InstallationProvider>
              <BrowserRouter>
                <Routes>
                  <Route path="/privacy-policy" element={<PrivacyPolicy />}></Route>
                  <Route path="/*" element={<Embedded />}></Route>
                </Routes>
              </BrowserRouter>
            </InstallationProvider>
          </Suspense>
        </AppProvider>
      </QueryClientProvider>
    </StrictMode>
  );
}

function Embedded() {
  const navigate = useNavigate();
  const navigateHandle = useCallback(
    (e?: BaseSyntheticEvent) => {
      if (e?.target.href) {
        e?.preventDefault();
        const url = new URL(e.target.href);
        navigate(url.pathname);
      }
    },
    [navigate],
  );
  const { plan, isLoading, isError } = useInstallation();

  if (isLoading) {
    return <FullPageLoader message="Recupero installazione ..." />;
  }
  if (isError) {
    return <FullPageError title="Qualcosa è andato storto" />;
  }
  return (
    <>
      <NavMenu>
        {/* 
          https://shopify.dev/docs/api/app-home/app-bridge-web-components/app-nav
          "The app name in the sidebar already links to your app's home route, which defaults to /, so there's no need to add a separate home link." 
        */}
        <Link onClick={navigateHandle} url="/history">
          Cronologia
        </Link>
        {plan === "avanzato" && (
          <>
            <Link onClick={navigateHandle} url="/listini/runs">
              Listini
            </Link>
            <Link onClick={navigateHandle} url="/import/products">
              Import Prodotti
            </Link>
          </>
        )}
        <Link onClick={navigateHandle} url="/settings">
          Impostazioni
        </Link>
        <Link onClick={navigateHandle} url="/plans">
          Piani
        </Link>
      </NavMenu>
      <ErrorBoundary fallback={<FullPageError title="Qualcosa è andato storto" />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/history" element={<SyncHistory />} />
          <Route path="/history/process/:processId" element={<SyncHistoryDetail />} />
          <Route path="/plans" element={<Plans />} />
          {plan === "avanzato" && (
            <>
              <Route path="/import/products" element={<ImportProducts />} />
              <Route path="/listini/runs" element={<ListiniRuns />} />
              <Route path="/listini/runs/:id/rows" element={<ListiniRunRows />} />
              <Route path="/settings/product-import" element={<DmodaImportProductsSettings />} />
              <Route path="/settings/price-import" element={<DmodaImportPriceSettings />} />
              <Route path="/settings/rma" element={<DmodaRmaSettings />} />
            </>
          )}
          {/* 
            Settings with Interstitial nav
            https://shopify.dev/docs/api/app-home/patterns/compositions/interstitial-nav
          */}
          <Route path="/settings" element={<SettingsHub />} />
          <Route path="/settings/general" element={<DmodaSyncSettings />} />
          <Route path="/settings/shipping" element={<DmodaShippingSettings />} />
        </Routes>
      </ErrorBoundary>
    </>
  );
}

export default App;
